import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { Option } from "@/lib/types/Options";

import styles from "./OptionsModal.module.scss";

import { Icon } from "../Icon/Icon";
import { Color } from "@/lib/types/Colors";
import { IconSize } from "@/lib/types/Icons";

type OptionsModalProps = {
  options: Option[];
  iconColor?: Color;
  iconSize?: IconSize;
};

export const OptionsModal = ({
  options,
  iconColor = "black",
  iconSize = "small",
}: OptionsModalProps): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, right: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node) &&
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom,
        right: window.innerWidth - rect.right,
      });
    }
    setIsOpen((prev) => !prev);
  };

  if (options.length === 0) {
    return <></>;
  }

  return (
    <div className={styles.trigger_wrapper}>
      <div ref={triggerRef} className={styles.trigger} onClick={handleToggle}>
        <Icon name="options" size={iconSize} color={iconColor} handleHover />
      </div>
      {isOpen &&
        createPortal(
          <div
            ref={modalRef}
            className={styles.portal_wrapper}
            style={{ top: position.top, right: position.right }}
          >
            <div className={styles.options_modal_wrapper} tabIndex={-1}>
              {options.map((option, index) => (
                <div key={index} className={styles.option_wrapper}>
                  <div
                    className={`${styles.option} ${
                      option.disabled ? styles.disabled : ""
                    }`}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      event.nativeEvent.stopImmediatePropagation();
                      setIsOpen(false);
                      option.onClick();
                    }}
                  >
                    <Icon name={option.iconName} color="white" size="normal" />
                    <span>{option.label}</span>
                  </div>
                  {index < options.length - 1 && (
                    <div className={styles.divider} />
                  )}
                </div>
              ))}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};
